import * as React from 'react';
import { memo } from 'react';
import styled, { css } from 'styled-components';

import { IPaletteRowItem } from './paletteTypes';

interface IPaletteRowProps {
  item: IPaletteRowItem;
  selected: boolean;
  onHover: () => void;
  onActivate: () => void;
}

/**
 * One palette row: leading icon, title + optional subtitle, trailing hint.
 * Activates on `mousedown` (not click) with `preventDefault` so the search input
 * keeps focus. `data-active` marks the selected row for `scrollIntoView`.
 *
 * Entity rows also carry `item.actions` — a strip of icon-buttons rendered on
 * the right, revealed when the row is keyboard-selected OR hovered (the idle
 * row shows the «actions available» hint instead, stacked in the same cell so
 * revealing the strip causes no layout shift). A button runs its own action;
 * it fires on `mousedown` with `stopPropagation` so the row-body primary action
 * (open card) never also fires, and is `tabIndex={-1}` so it stays out of the
 * roving keyboard selection (full keyboard access lives in the →/Tab drill-in).
 */
export const PaletteRow = memo<IPaletteRowProps>(
  ({ item, selected, onHover, onActivate }) => {
    const Icon = item.icon;
    const actions = item.actions ?? [];
    const hasActions = actions.length > 0;
    return (
      <Row
        data-active={selected || undefined}
        selected={selected}
        onMouseMove={onHover}
        onMouseDown={(event) => {
          event.preventDefault();
          onActivate();
        }}
      >
        <IconWrap selected={selected}>
          <Icon size={16} strokeWidth={2} />
        </IconWrap>
        <Texts>
          <Title>{item.title}</Title>
          {item.subtitle ? <Subtitle>{item.subtitle}</Subtitle> : null}
        </Texts>
        {hasActions ? (
          <Trailing>
            {item.hint ? <StackedHint>{item.hint}</StackedHint> : null}
            <Actions>
              {actions.map((action) => {
                const ActionIcon = action.icon;
                return (
                  <ActionButton
                    key={action.id}
                    type="button"
                    tabIndex={-1}
                    title={action.title}
                    aria-label={action.title}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      action.run();
                    }}
                  >
                    <ActionIcon size={15} strokeWidth={2} />
                  </ActionButton>
                );
              })}
            </Actions>
          </Trailing>
        ) : item.keyBadge ? (
          <KeyBadge>{item.keyBadge}</KeyBadge>
        ) : item.hint ? (
          <Hint>{item.hint}</Hint>
        ) : null}
      </Row>
    );
  },
);

/** Trailing cell: the hint and the action strip are stacked in the SAME grid
 *  cell (both `grid-area: 1 / 1`) so swapping one for the other on
 *  select / hover never reflows the row. The cell sizes to the wider child. */
const Trailing = styled.div`
  display: grid;
  align-items: center;
  justify-items: end;
  flex-shrink: 0;
  margin-left: 8px;

  & > * {
    grid-area: 1 / 1;
  }
`;

const StackedHint = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-c6ccd0);
  white-space: nowrap;
  pointer-events: none;
  transition: opacity 0.12s ease;
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: var(--k-fg-616e78);
  background: transparent;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    color: var(--k-fg-0d53fc);
    background: var(--k-bg-ffffff);
  }
`;

const revealActions = css`
  ${Actions} {
    opacity: 1;
    pointer-events: auto;
  }

  ${StackedHint} {
    opacity: 0;
  }
`;

const Row = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ selected }) =>
    selected ? 'var(--k-bg-e7eeff)' : 'transparent'};

  ${({ selected }) => (selected ? revealActions : '')}

  &:hover {
    ${revealActions}
  }
`;

const IconWrap = styled.div<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  color: ${({ selected }) =>
    selected ? 'var(--k-fg-0d53fc)' : 'var(--k-fg-616e78)'};
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--k-fg-263446);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.span`
  font-size: 12px;
  color: var(--k-fg-616e78);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Hint = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-c6ccd0);
`;

/** Hotkey chip on a page-action row (e.g. `Alt+C` / `⌥C`; mirrors the
 *  cheat-sheet Kbd). `white-space: nowrap` keeps the wider Alt label on one line. */
const KeyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid var(--k-border-dfe6f1);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--k-fg-263446);
  background: var(--k-bg-fafbfd);
`;
